import {
  CircularProgress,
  Typography
} from "@mui/material";
import { Box } from "@mui/system";
import { BarChart } from "@mui/x-charts/BarChart";
import { useMemo } from "react";

import { BarChartData } from "../../types/leads.types";

/**
 * Generic Bar Chart Component
 * 
 * @param data - Object with string keys and number values (e.g., { new: 10, qualified: 20, won: 15 })
 * @param isLoading - Show loading spinner when true
 * @param title - Chart title
 * @param color - Bar color (default: "#ff8c00")
 * 
 * Example usage:
 * <LeadsBarChart 
 *   data={{ new: 10, qualified: 20, won: 15 }} 
 *   title="Lead Pipeline" 
 *   color="#ff8c00"
 * />
 */
interface Props {
  data?: BarChartData | null;
  isLoading?: boolean;
  title?: string;
  color?: string;
  hasError?: boolean;
}

export default function LeadsBarChart({
  data,
  isLoading = false,
  title = "Chart",
  color = "#ff8c00",
  hasError = false,
}: Props) {
  const xLabels = useMemo(() => {
    if (!data || !data.length) return [];
  
    return data?.map(item =>
      item.label.replace(/([A-Z])/g, " $1").replace(/^./, str => str.toUpperCase())
    );
  }, [data]);

  const chartData = useMemo(() => {
    if (!data || !data?.length) return [];
    return data?.map(item =>
      item.value);
  }, [data]);


  return (
    <Box sx={{
      width: "100%",
      height: "450px",
      border: "solid 1px #0000001F",
      borderRadius: "24px",
      display: "flex",
      flexDirection: "column",
      overflow: "hidden"
    }}>
      <Box sx={{ padding: "16px 16px 0 16px" }}>
        <Typography variant="title_medium">{title}</Typography>
      </Box>
      {
        isLoading ? (<Box sx={{ width: "100%", height: "100%", display: "flex", justifyContent: "center", alignItems: "center" }}>
          <CircularProgress />
        </Box>) : hasError ? (<Box sx={{ width: "100%", height: "100%", display: "flex", justifyContent: "center", alignItems: "center" }}>
          <Typography color="text.secondary">
            Error in displaying graph data
          </Typography>
        </Box>) : !data || Object.keys(data).length === 0 ? (
          <Box sx={{ width: "100%", height: "100%", display: "flex", justifyContent: "center", alignItems: "center" }}>
            <Typography color="text.secondary">
              No data available
            </Typography>
          </Box>
        ) : (
          <Box sx={{ flex: 1, width: "100%", minHeight: 0 }}>
            <BarChart
              series={[
                {
                  data: chartData,
                  id: "barChartData",
                  color: color
                },
              ]}
              xAxis={[{
                data: xLabels,
                scaleType: "band",
                tickLabelStyle: {
                  angle: -45,
                  textAnchor: "end",
                  fontSize: 11,
                  fill: "#666"
                }
              }]}
              yAxis={[{
                valueFormatter: (value: number) => {
                  if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
                  if (value >= 1000) return `${(value / 1000).toFixed(0)}K`;
                  return value.toString();
                }
              }]}
              height={380}
              margin={{ left: 60, right: 25, top: 35, bottom: 100 }}
              sx={{
                width: "100%",
                "& .MuiChartsAxis-tickLabel": {
                  fontSize: "0.75rem",
                  fill: "#666",
                },
                "& .MuiChartsAxis-line": {
                  stroke: "#e0e0e0",
                },
                "& .MuiChartsGrid-line": {
                  stroke: "#f0f0f0",
                },
              }}
            />
          </Box>
        )
      }

    </Box>
  )
}